<template>
    <div>
        <PositionAttachedRolesTable 
            :pagination="paginationComputed" 
            :useNewBadge="true" 
            :dataStream="rolesData" 
            :initialFilters="appliedFilters"
            :positionData="positionDataObject"
            :chatterData="chatterData"
            :totalChatterData="totalChatterData"
            @onSearchChange="handleSearchChange"
            @onFilterChange="handleFilterChange"
            @onPageChange="handlePageChange"
            @onRowOperation="handleRowOperation"
            @onRoleDrillDown="handleRoleDrillDown"
            @openChatterDrawer="handleOpenChatterDrawer"
            @detach="handleDetach"
            @requestMoreCheckpointsChatter="handleRequestMoreCheckpointsChatter"
        />
    </div>
</template>

<script setup>
import { defineProps, ref, onMounted, computed, getCurrentInstance } from "vue";
import PositionAttachedRolesTable from "@components/PositionAttachedRolesTable/PositionAttachedRolesTable.vue";
import { getListOfRolesAttachedToPosition, getChatterList, detachOrgObject } from "@/api";

const instance = getCurrentInstance();
const $loader = instance?.proxy?.$loader;

const props = defineProps({
    positionId: {
        type: [Number, String],
        required: true,
    },
    positionName: {
        type: String,
        default: '',
    },
});

const rolesData = ref([]);
const paginationModel = ref({
    page: 1,
    total: 0,
    perPage: 10,
});

const searchQuery = ref("");
const appliedFilters = ref(["draft", "active"]);
const accountId = ref(null);
const isLoading = ref(false);

// Chatter related state
const chatterData = ref([]);
const totalChatterData = ref(0);
const loadedChatterPages = ref({});
const currentChatterRoleId = ref(null);

// Create position data object for the table component (reactive)
const positionDataObject = computed(() => ({
    id: props.positionId,
    name: props.positionName,
}));

// Create a computed for pagination to ensure reactivity
const paginationComputed = computed(() => ({
    page: paginationModel.value.page,
    total: paginationModel.value.total,
    perPage: paginationModel.value.perPage,
}));

function parseFilters(filters) {
    const parsedFilters = {};
    
    const statusFilters = filters.filter(f => 
        ['draft', 'active', 'retired', 'inactive'].includes(f)
    );
    if (statusFilters.length > 0) {
        parsedFilters.status = statusFilters;
    }
    
    if (filters.includes('show_archived')) {
        parsedFilters.archived = true;
    } else {
        parsedFilters.archived = false;
    }
    
    if (filters.includes('no_positions')) {
        parsedFilters.positions = 'no';
    }
    if (filters.includes('no_responsibilities')) {
        parsedFilters.responsibilities = 'no';
    }
    
    if (filters.includes('recently_activated')) {
        parsedFilters.recently_activated = true;
    }
    if (filters.includes('recently_retired')) {
        parsedFilters.recently_retired = true;
    }
    if (filters.includes('recently_created')) {
        parsedFilters.recently_created = true;
    }
    
    return parsedFilters;
}

async function fetchRoles() {
    if (!accountId.value || !props.positionId) {
        return;
    }
    
    try {
        isLoading.value = true;
        $loader?.show();
        
        const offset = (paginationModel.value.page - 1) * paginationModel.value.perPage;
        const filters = parseFilters(appliedFilters.value);
        
        const response = await getListOfRolesAttachedToPosition(
            accountId.value,
            paginationModel.value.perPage,
            offset,
            searchQuery.value,
            filters,
            props.positionId
        );
        
        if (response?.data) {
            rolesData.value = response.data.results.map((el) => ({
                id: el.id || null,
                role_code: el.code || "",
                role_name: el.name || "",
                status: el.status || "inactive",
                attached_position_count: el.position_attachment_count || 0,
                attached_res_count: el.responsibility_attachment_count || 0,
                active_attached_position_count: el.active_attached_position_count || 0,
                inactive_attached_position_count: el.inactive_attached_position_count || 0,
                active_attached_responsibilities_count: el.active_attached_responsibilities_count || 0,
                inactive_attached_responsibilities_count: el.inactive_attached_responsibilities_count || 0,
                archived: el.archived || false,
                draft_responsibilities_count: el.draft_responsibilities_count || 0,
            }));
            paginationModel.value.total = response.data.count || 0;
        } else {
            rolesData.value = [];
            paginationModel.value.total = 0;
        }
    } catch (error) {
        console.error('Error fetching roles:', error);
        rolesData.value = [];
        paginationModel.value.total = 0;
    } finally {
        isLoading.value = false;
        $loader?.hide();
    }
}

function handleSearchChange(searchText) {
    searchQuery.value = searchText;
    paginationModel.value.page = 1;
    fetchRoles();
}

function handleFilterChange(filters) {
    appliedFilters.value = filters;
    paginationModel.value.page = 1;
    fetchRoles();
}

function handlePageChange(pageData) {
    paginationModel.value.page = pageData.page;
    // perPage is maintained in paginationModel, only update if provided
    if (pageData.perPage) {
        paginationModel.value.perPage = pageData.perPage;
    }
    fetchRoles();
}

async function handleRowOperation(operation) {
    console.log('Row operation:', operation);
    // Refresh the list after any operation
    await fetchRoles();
    // Emit event to parent to refetch position data and update count
    emit('refresh-position-data');
}

const emit = defineEmits(['drilldown', 'refresh-position-data']);

function handleRoleDrillDown(roleData) {
    const drilldownPayload = {
        roleId: roleData.id,
        tab: 'purpose',
        breadcrumbs: roleData.breadcrumbs
    };
    emit('drilldown', drilldownPayload);
}

async function handleOpenChatterDrawer(data) {
    if (!data || !data.id) return;
    
    const roleId = data.id;
    
    // If switching to a different role, reset the chatter data
    if (currentChatterRoleId.value !== roleId) {
        chatterData.value = [];
        totalChatterData.value = 0;
        loadedChatterPages.value = {};
        currentChatterRoleId.value = roleId;
    }
    
    await fetchChatterData(roleId);
}

async function fetchChatterData(roleId) {
    if (!roleId || !accountId.value) return;
    
    const currentLength = chatterData.value.length;
    const page = Math.floor(currentLength / 10) + 1;
    
    // Initialize page tracking for this role if needed
    if (!loadedChatterPages.value[roleId]) {
        loadedChatterPages.value[roleId] = new Set();
    }
    
    // Don't reload already loaded pages
    if (loadedChatterPages.value[roleId].has(page)) {
        return;
    }
    
    try {
        const { data } = await getChatterList(accountId.value, roleId, "role", page);
        loadedChatterPages.value[roleId].add(page);
        
        if (data && data.count) {
            totalChatterData.value = data.count;
        }
        
        if (data && data.results) {
            const chatterItems = data.results.map((el) => {
                const date = new Date(el.recorded_on);
                const milliseconds = date.getTime();
                const millisecondsString = milliseconds.toString();
                const nameParts = el.recorded_by_name.split(" ");
                
                const newEl = {
                    id: el.id,
                    IsExIQtiveBot: false,
                    message: el.note,
                    created_on: millisecondsString,
                    IsHistory: el.is_history,
                };
                
                if (nameParts.length === 2) {
                    newEl.avatar = {
                        picture: "",
                        firstName: nameParts[0],
                        lastName: nameParts[1],
                    };
                } else {
                    newEl.avatar = {
                        picture: "",
                        firstName: el.recorded_by_name,
                        lastName: "",
                    };
                }
                
                return newEl;
            });
            chatterData.value.push(...chatterItems);
        }
    } catch (error) {
        console.error('Error fetching chatter data:', error);
    }
}

async function handleDetach(detachData) {
    try {
        // Note: The modal already sets $loader.setAppLoading(true) when detach is clicked
        
        const targetObj = detachData.target;
        const detachObj = detachData.detachObj;
        
        if (!accountId.value) {
            accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
        }
        
        if (targetObj.objectType.toLowerCase() === "role") {
            if (detachObj.type.toLowerCase() === "position") {
                // Detach role from position
                const payload = {
                    childType: targetObj.objectType.toLowerCase(),
                    childId: targetObj.objectId,
                    parentData: {
                        parent_id: detachObj.id,
                    },
                };
                await detachOrgObject(accountId.value, payload);
            } else if (detachObj.type.toLowerCase() === "responsibility") {
                // Detach responsibility from role
                const payload = {
                    childType: "responsibility",
                    childId: detachObj.id,
                    parentData: {
                        parent_id: targetObj.objectId,
                    },
                };
                await detachOrgObject(accountId.value, payload);
            }
        }
        
        // Reload the roles data to reflect the changes
        await fetchRoles();
        
        // Emit event to parent to refetch position data and update count
        emit('refresh-position-data');
        
        // Call the callback function to close the modal AFTER reloading data
        if (detachData.callBackFunc) {
            detachData.callBackFunc();
        }
    } catch (error) {
        console.error("Error detaching:", error);
    } finally {
        // Turn off the loader to re-enable buttons
        instance?.proxy?.$loader.setAppLoading(false);
    }
}

async function handleRequestMoreCheckpointsChatter(data) {
    if (currentChatterRoleId.value) {
        await fetchChatterData(currentChatterRoleId.value);
    }
}


// Expose method to parent to refresh roles data
function refreshRoles() {
    return fetchRoles();
}

// Expose the refresh method to parent components
defineExpose({
    refreshRoles
});

onMounted(async () => {
    try {
        accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
        if (props.positionId && accountId.value) {
            await fetchRoles();
        }
    } catch (error) {
        console.error('Error initializing component:', error);
    }
});
</script>

<style lang="scss" scoped>

</style>